/**
 * Idempotency-Key handling for commands that must never run twice (issue, accept, payments, deliveries…).
 * A key belongs to an intent (scope) and to the exact payload: retrying the same payload reuses the key, so the
 * server returns the first result; a changed payload gets a new key. The key is cleared after success.
 */
const PREFIX = 'dec.idem.';

export function uuid(): string {
  // crypto.randomUUID only exists on secure origins; a tablet on the LAN over http lacks it.
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6]! & 0x0f) | 0x40;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/** Stable JSON (sorted keys), so the same payload always hashes the same. */
export function stableJson(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableJson).join(',')}]`;
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : 1));
  return `{${entries.map(([k, v]) => JSON.stringify(k) + ':' + stableJson(v)).join(',')}}`;
}

function hash(text: string): string {
  // FNV-1a: only used to notice a changed payload, not for security.
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16);
}

interface Stored {
  key: string;
  hash: string;
}

export function intentKey(scope: string, payload: unknown): string {
  const h = hash(stableJson(payload));
  try {
    const raw = sessionStorage.getItem(PREFIX + scope);
    const stored = raw ? (JSON.parse(raw) as Stored) : null;
    if (stored && stored.hash === h) return stored.key;
    const key = `web:${uuid()}`;
    sessionStorage.setItem(PREFIX + scope, JSON.stringify({ key, hash: h } satisfies Stored));
    return key;
  } catch {
    return `web:${uuid()}`;
  }
}

export function clearIntent(scope: string): void {
  try {
    sessionStorage.removeItem(PREFIX + scope);
  } catch {
    // ignore
  }
}
